/**
 * The confidence tag the brief asks for on every finding card:
 * `deterministisch/hoch · retrieval/mittel · Indikator/tief`.
 *
 * It answers "how did this come about?", never "how likely is it correct?".
 * And it is derived, not guessed: each tag is a property of the API lane an
 * item arrives in, and the lanes are enforced by the backend schema —
 *
 *   `Finding`            → deterministisch / hoch
 *                          a verified rule on a verified zone, compared against
 *                          its threshold by deterministic code.
 *   `IndicativeFinding`  → retrieval / mittel
 *                          the rule or the zone is not verified against the
 *                          primary source yet; it carries no severity at all.
 *   `Hinweis`            → Indikator / tief
 *                          an experience value without legal consequence.
 *
 * Nothing here parses a German message string, and nothing infers a tag from a
 * rule ID. If the backend ever grew a real per-finding confidence field, this
 * table is the one place that would have to move.
 *
 * Rule inherited from the provenance badges: a confidence tag may NEVER use a
 * traffic-light token. "This was computed deterministically" is not "this rule
 * is violated" — a reader who learns to read one palette as the other has been
 * taught something false. Three tags, three glyphs, three words, so the
 * distinction survives greyscale and the printed page.
 */
import { Radar, ScrollText, SquareSigma } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { TranslationKey } from '@/i18n'

/** The lane an item came out of — the only input the tag is derived from. */
export type ReportLane = 'finding' | 'indicative' | 'hinweis'

export type ConfidenceBasis = 'deterministic' | 'retrieval' | 'indicator'

export interface ConfidenceLook {
  icon: LucideIcon
  /** "deterministisch" | "retrieval" | "Indikator" */
  basis: TranslationKey
  /** "hoch" | "mittel" | "tief" */
  level: TranslationKey
  meaning: TranslationKey
  /** 1 = tief, 2 = mittel, 3 = hoch — the meter, which is decorative. */
  steps: 1 | 2 | 3
  className: string
}

export const CONFIDENCE_BASES: readonly ConfidenceBasis[] = [
  'deterministic',
  'retrieval',
  'indicator',
]

export const CONFIDENCE_LOOK: Record<ConfidenceBasis, ConfidenceLook> = {
  // Computed by code against a cited threshold. Solid border, ink — factual.
  deterministic: {
    icon: SquareSigma,
    basis: 'confidence.deterministic',
    level: 'confidence.level.high',
    meaning: 'confidence.deterministic.meaning',
    steps: 3,
    className: 'border-border-strong bg-white text-ink-2',
  },
  // Came out of a pack that is not verified against the primary source here.
  // Dashed — the same visual language the indicative lane uses.
  retrieval: {
    icon: ScrollText,
    basis: 'confidence.retrieval',
    level: 'confidence.level.medium',
    meaning: 'confidence.retrieval.meaning',
    steps: 2,
    className: 'border-dashed border-border-strong bg-surface-2 text-muted',
  },
  // An indicator, not a rule. Dotted, quietest of the three.
  indicator: {
    icon: Radar,
    basis: 'confidence.indicator',
    level: 'confidence.level.low',
    meaning: 'confidence.indicator.meaning',
    steps: 1,
    className: 'border-dotted border-border-strong bg-white text-muted-2',
  },
}

/** The whole derivation. One switch, no heuristics, no string parsing. */
export function confidenceOf(lane: ReportLane): ConfidenceBasis {
  switch (lane) {
    case 'finding':
      return 'deterministic'
    case 'indicative':
      return 'retrieval'
    case 'hinweis':
      return 'indicator'
  }
}
